import { Controller, useFormContext } from "react-hook-form";
import { SHStack } from "@components/design-systems/sh-stack";
import { SHTextField } from "@components/design-systems/sh-text-field";
import {
  AnalysisSetupDTO,
  AnalysisSetupStepRenderedFieldProps,
} from "@models/platform-analysis/entities/steps/setup";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { debounce, isEmpty } from "lodash";
import { useEffect, useMemo } from "react";

export const AnalysisSetupStepTargetAdviserField = ({
  disabled,
}: AnalysisSetupStepRenderedFieldProps) => {
  const {
    control,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext<AnalysisSetupDTO>();
  const { setBannerName } = usePlatformAnalysis();

  const watchedAdviserName = watch("adviserName") || "";
  const watchedPracticeName = watch("practiceName") || "";

  const isEmptyAdviserName = useMemo(
    () => isEmpty(watchedAdviserName.trim()),
    [watchedAdviserName],
  );
  const isEmptyPracticeName = useMemo(
    () => isEmpty(watchedPracticeName.trim()),
    [watchedPracticeName],
  );

  const debounceHandleOnNameChange = useMemo(
    () =>
      debounce((value: string) => {
        trigger("practiceName").then(() =>
          setBannerName({ adviserName: value }),
        );
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const debounceHandleOnPracticeNameChange = useMemo(
    () =>
      debounce((value: string) => {
        trigger("adviserName").then(() =>
          setBannerName({ practiceName: value }),
        );
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    return () => {
      debounceHandleOnNameChange.cancel();
      debounceHandleOnPracticeNameChange.cancel();
    };
  }, [debounceHandleOnNameChange, debounceHandleOnPracticeNameChange]);

  return (
    <SHStack
      spacing={3}
      direction="column"
      sx={{ width: { xs: "100%", md: 520 } }}
    >
      <Controller
        name="adviserName"
        control={control}
        render={({ field }) => (
          <SHTextField
            {...field}
            required={!isEmptyPracticeName}
            sx={{ width: "100%" }}
            label={"Adviser name"}
            placeholder={"Enter adviser name"}
            disabled={disabled}
            error={!!errors.adviserName}
            maxLength={100}
            hasCounter
            helperText={errors?.adviserName?.message}
            onChange={({ target: { value } }) => {
              field.onChange(value);
              debounceHandleOnNameChange(value);
            }}
          />
        )}
      />

      <Controller
        name="practiceName"
        control={control}
        render={({ field }) => (
          <SHTextField
            {...field}
            required={!isEmptyAdviserName}
            sx={{ width: "100%" }}
            label={"Practice name"}
            placeholder={"Enter practice name"}
            disabled={disabled}
            maxLength={100}
            hasCounter
            error={!!errors.practiceName}
            helperText={errors?.practiceName?.message}
            onChange={({ target: { value } }) => {
              field.onChange(value);
              debounceHandleOnPracticeNameChange(value);
            }}
          />
        )}
      />
    </SHStack>
  );
};
